/** The tenant's dashboard two-step sign-in (dashboard password MFA plan §3.2): Optional or
 *  Mandatory for the dashboard's password sign-in. SSO sign-in is not affected.
 *
 *  - **Optional**: users may set up an authenticator app or a text-message code.
 *  - **Mandatory**: every password user sets one up at the next sign-in and passes it at every
 *    password sign-in. It takes effect at the save (no grace): password sessions made without it
 *    are refused from their next request, so the switch asks first, naming the counts.
 *  - A franchise parent's Mandatory covers its licensees: a licensee shows it as inherited and
 *    cannot change it here.
 *  - MCP auto-apply (owner 2026-10-21): the mode never turns it on or off. It decides whether
 *    password sign-ins prove a second factor, i.e. whether their MCP changes apply directly (the
 *    AI editing tab's switch on) or wait on the review page; SSO sign-ins always count.
 *
 *  Changing it needs `console_tenant_security_manage` (the payload's `can_edit`) and a reason
 *  (the config history keeps it); viewing needs `console_top_view`. */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import {
  DASHBOARD_MFA_MODE_LABEL,
  DASHBOARD_MFA_REASON_MAX,
  dashboardMfa,
  dashboardMfaKeys,
  updateDashboardMfa,
  type DashboardMfaMode,
  type TourOperatorDashboardMfa,
} from '@/api/dashboardMfa';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Field, SectionCard } from '@/components/ReadView';
import { useSnackbar } from '@/components/Snackbar';

const MODES: { value: DashboardMfaMode; hint: string }[] = [
  { value: 'optional', hint: 'users may set up an authenticator app or a text-message code' },
  {
    value: 'mandatory',
    hint: 'every password user sets one up at their next sign-in and passes it at every password sign-in',
  },
];

/** An MCP connection keeps the sign-in it was made with (its `amr`) until it reconnects. */
const EARLIER = '(an MCP connection made earlier keeps its own sign-in until it reconnects)';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function SignInSecurityTab({ topId }: { topId: number }) {
  const query = useQuery({
    queryKey: dashboardMfaKeys.settings(topId),
    queryFn: () => dashboardMfa(topId),
  });
  if (query.isPending) return <CircularProgress />;
  if (query.isError) {
    return (
      <Alert severity="error">
        Couldn’t load the sign-in security settings: {query.error.message}
      </Alert>
    );
  }
  return <SignInSecurityForm settings={query.data} />;
}

/** What the switch to Mandatory does, from the GET's counts. */
export function mandatoryConfirmText(settings: TourOperatorDashboardMfa): string {
  const sessions = settings.password_sessions_without_mfa;
  const users = settings.staff.not_enrolled;
  if (!settings.enforced) {
    return (
      'The dashboard does not enforce this yet. Once it does, password sessions without two-step ' +
      `sign-in are signed out (${sessions} now), and users who have not set it up (${users} now) ` +
      'must do so at their next sign-in.'
    );
  }
  return (
    `${plural(sessions, 'password session is', 'password sessions are')} signed out now. ` +
    `${plural(users, 'user has', 'users have')} not set up two-step sign-in and must do so at their next sign-in.`
  );
}

function SignInSecurityForm({ settings }: { settings: TourOperatorDashboardMfa }) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const topId = settings.tour_operator_id;
  const [mode, setMode] = useState<DashboardMfaMode>(settings.mode);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const parent = settings.inherited_from;
  // A licensee covered by its parent's Mandatory shows what is in effect, read-only.
  const shown: DashboardMfaMode = parent ? settings.effective_mode : mode;
  const editable = settings.can_edit && !parent;
  const dirty = mode !== settings.mode;

  const mutation = useMutation({
    mutationFn: () => updateDashboardMfa(topId, { mode, reason }),
    onSuccess: (next) => {
      queryClient.setQueryData(dashboardMfaKeys.settings(topId), next);
      // The save wrote the tenant's config: its change history and the record the
      // Configuration tab edits are stale now.
      void queryClient.invalidateQueries({ queryKey: ['tourOperators', topId, 'config_changes'] });
      void queryClient.invalidateQueries({ queryKey: ['tourOperators', 'detail', topId] });
      setMode(next.mode);
      setReason('');
      const signedOut =
        next.changed && next.enforced && next.sessions_refused > 0
          ? ` ${plural(next.sessions_refused, 'password session', 'password sessions')} signed out.`
          : '';
      snackbar.show(
        next.changed
          ? `Saved — the change is in the config history.${signedOut}`
          : 'Nothing to save: the mode was already set.',
        next.changed ? 'success' : 'info',
      );
    },
  });
  const error =
    mutation.error instanceof ConsoleApiError
      ? ((mutation.error.body as { detail?: string } | undefined)?.detail ?? mutation.error.message)
      : mutation.error?.message;

  const onSave = () => {
    if (mode === 'mandatory') setConfirming(true);
    else mutation.mutate();
  };

  const inEffect = parent
    ? `Mandatory (inherited from ${parent.name})`
    : DASHBOARD_MFA_MODE_LABEL[settings.effective_mode];

  return (
    <>
      <SectionCard title="Dashboard two-step sign-in">
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Applies to the dashboard’s password sign-in; SSO sign-in is not affected.
          </Typography>
          {!settings.enforced && (
            <Alert severity="info">
              The dashboard does not enforce this yet: the setting is stored, and the counts below
              say what it would do.
            </Alert>
          )}
          {!settings.password_sign_in && (
            <Alert severity="info">
              Password sign-in is off for this tenant; this setting has no effect.
            </Alert>
          )}
          <Field label="In effect" value={inEffect} />
          {parent && (
            <Alert severity="info">
              The franchise parent’s Mandatory covers this tenant, so it cannot be changed here.
              Change it on{' '}
              <Link component={RouterLink} to={`/tour-operators/${parent.id}/sign-in-security`}>
                {parent.name}’s Sign-in security tab
              </Link>
              .
            </Alert>
          )}
          <FormControl disabled={!editable}>
            <FormLabel id="dashboard-mfa-mode">Mode</FormLabel>
            <RadioGroup
              aria-labelledby="dashboard-mfa-mode"
              value={shown}
              onChange={(e) => setMode(e.target.value as DashboardMfaMode)}
            >
              {MODES.map((m) => (
                <FormControlLabel
                  key={m.value}
                  value={m.value}
                  control={<Radio />}
                  label={`${DASHBOARD_MFA_MODE_LABEL[m.value]} — ${m.hint}`}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {editable ? (
            <>
              <TextField
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                fullWidth
                inputProps={{ maxLength: DASHBOARD_MFA_REASON_MAX }}
                helperText="Recorded in the tenant's config history."
              />
              {mutation.isError && <Alert severity="error">{error}</Alert>}
              <Box>
                <Button
                  variant="contained"
                  disabled={!dirty || !reason.trim() || mutation.isPending}
                  onClick={onSave}
                >
                  Save
                </Button>
              </Box>
            </>
          ) : (
            !settings.can_edit && (
              <Typography variant="body2" color="text.secondary">
                Changing it needs the console_tenant_security_manage permission.
              </Typography>
            )
          )}
        </Stack>
      </SectionCard>

      <SectionCard title="Who it affects">
        <AffectedFacts settings={settings} shown={shown} />
      </SectionCard>

      {confirming && (
        <ConfirmDialog
          open
          title="Make two-step sign-in mandatory?"
          message={mandatoryConfirmText(settings)}
          confirmLabel="Make mandatory"
          destructive
          onConfirm={() => {
            setConfirming(false);
            mutation.mutate();
          }}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
}

function AffectedFacts({
  settings,
  shown,
}: {
  settings: TourOperatorDashboardMfa;
  shown: DashboardMfaMode;
}) {
  const { staff, auto_apply: autoApply } = settings;
  const passwordMethods = settings.auth_methods
    .filter((m) => m.backend !== 'auth0')
    .map((m) => m.name ?? m.backend ?? 'Password');
  // What the mode shown means for MCP changes from a password sign-in, while auto-apply is on
  // (off, every MCP change waits whatever the mode; SSO-only, the mode does not matter). A grant
  // keeps the amr of its own sign-in for its refresh life, so Mandatory holds for new sign-ins.
  const effect =
    !settings.password_sign_in || !autoApply.configured
      ? null
      : shown === 'mandatory'
        ? settings.enforced
          ? `Mandatory: every password sign-in from now on passes two-step sign-in, so its MCP changes apply directly ${EARLIER}.`
          : `Mandatory: once the dashboard enforces it, every password sign-in passes two-step sign-in, so its MCP changes apply directly ${EARLIER}.`
        : 'Optional: MCP changes from a password sign-in without two-step sign-in wait on the review page; sign-ins with it or SSO still apply directly.';

  return (
    <>
      <Field
        label="Password sign-in"
        value={
          settings.password_sign_in
            ? `Offered: ${passwordMethods.join(', ') || 'Password'}`
            : 'Off (SSO only)'
        }
      />
      {settings.password_sign_in && (
        <>
          <Field
            label="Staff"
            value={
              <>
                {`${staff.password_users} with a password: ${staff.enrolled} set up ` +
                  `(authenticator app ${staff.by_method.totp}, text message ${staff.by_method.sms}), ` +
                  `${staff.not_enrolled} not set up`}
                {staff.not_enrolled > 0 && (
                  <Box>
                    <Link
                      component={RouterLink}
                      to={`/tour-operators/${settings.tour_operator_id}/staff?has_mfa=false`}
                    >
                      Show the staff without two-step sign-in
                    </Link>
                  </Box>
                )}
              </>
            }
          />
          <Field
            label="Live sessions"
            value={plural(
              settings.password_sessions_without_mfa,
              'password session without two-step sign-in',
              'password sessions without two-step sign-in',
            )}
          />
        </>
      )}
      <Field
        label="MCP auto-apply"
        value={
          !autoApply.configured
            ? 'Off — MCP changes wait on the review page.'
            : autoApply.mfa_capable_login
              ? 'On — every dashboard sign-in proves a second factor, so MCP changes apply directly.'
              : 'On — MCP changes from sign-ins with two-step sign-in or SSO apply directly; ' +
                `a password sign-in without it waits on the review page — ${autoApply.reason}.`
        }
      />
      {effect && (
        <Typography variant="body2" color="text.secondary">
          {effect}
        </Typography>
      )}
    </>
  );
}
